import React, { useMemo } from 'react';
import _ from 'underscore';
import { useI18nContext } from '@bingads-webui/copilot-i18n';

export type SingleResultProps = {
  className?: string;
  currency?: string;
  title: string;
  content: any;
  type?: 'number' | 'currency' | 'decimal' | 'percent';
  impact?: string;
  percentShift?: number;
};
const MAX_DISPLAYABLE_PERCENT_SHIFT = 1000000000000000;

export const SingleResult: React.FC<SingleResultProps> = React.memo((props) => {
  const {
    className,
    currency,
    title,
    content,
    type = 'number',
    impact,
    percentShift,
  } = props;

  const i18n = useI18nContext();

  const formattedContent = useMemo<string | undefined>(() => {
    if (content === undefined || content === null) {
      return undefined;
    }

    switch (type) {
      case 'currency':
        return i18n?.formatCurrency(content, currency);
      case 'decimal':
        return i18n?.formatDecimalToFixed(content);
      case 'percent':
        return i18n?.formatPercent(content / 100, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
      case 'number':
      default:
        return i18n?.formatDecimal(content);
    }
  }, [content, currency, i18n]);

  const formattedPercentShift = useMemo<string | undefined>(() => {
    if (!Number.isFinite(percentShift)) {
      return undefined;
    }
    if (percentShift === 0) {
      return ' (0%)';
    }
    return ` (${i18n.formatPercent(percentShift / 100, { withSign: 'always', maximumFractionDigits: 2, minimumFractionDigits: 2 })})`;
  }, [percentShift]);

  if (!formattedContent) {
    return null;
  }

  return (
    <div className={className}>
      <strong>{title}</strong>
      {' '}
      <span className="formatted-content">{formattedContent}</span>
      {_.isNumber(percentShift) && percentShift < MAX_DISPLAYABLE_PERCENT_SHIFT && (
        <span className={impact}>{formattedPercentShift}</span>
      )}
    </div>
  );
});
